import { api } from './api'
import type {
  LoginRequest,
  LoginResponse,
  User,
  RefreshTokenRequest,
  RefreshTokenResponse,
  TokenInfo,
  CSRFTokenInfo,
} from '@/types/api'
import { getTokenInfo, isTokenExpired, shouldRefreshToken, getCombinedTokenInfo } from '@/utils/jwt'
import { csrfService } from '@/services/csrfService'

export const authService = {
  // Login user
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/user/login', credentials)

    // Store tokens and user data
    localStorage.setItem('auth_token', response.token)
    localStorage.setItem('refresh_token', response.refresh_token)
    localStorage.setItem('user', JSON.stringify(response.user))

    // Store token expiration info if available
    if (response.expires_in) {
      const expiresAt = Date.now() + response.expires_in * 1000
      localStorage.setItem('token_expires_at', expiresAt.toString())
    }

    // Store CSRF token if provided and CSRF is enabled
    if (response.csrf_token && csrfService.isEnabled()) {
      csrfService.storeToken(response.csrf_token, response.expires_in)
    }

    return response
  },

  // Logout user
  async logout(): Promise<void> {
    try {
      await api.post('/auth/user/logout')
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error)
    } finally {
      // Clear local storage
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('token_expires_at')
      localStorage.removeItem('user')

      // Clear CSRF token
      csrfService.clearToken()
    }
  },

  // Logout from all devices
  async logoutAll(): Promise<void> {
    try {
      await api.post('/auth/logout-all')
    } catch (error) {
      console.warn('Logout all API call failed:', error)
    } finally {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('token_expires_at')
      localStorage.removeItem('user')

      // Clear CSRF token
      csrfService.clearToken()
    }
  },

  // Get current user
  async getCurrentUser(): Promise<User> {
    return await api.get<User>('/auth/user/profile')
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = localStorage.getItem('auth_token')
    const user = localStorage.getItem('user')

    if (!token || !user) {
      return false
    }

    // Detect token type and validate accordingly
    const tokenType = this.detectTokenType(token)

    if (tokenType === 'jwt') {
      // Check if JWT token is expired
      const expired = isTokenExpired(token)
      if (expired) {
        return false
      }
    } else if (tokenType === 'sanctum') {
      // For Sanctum tokens, check expiration using stored timestamp
      const expiresAt = localStorage.getItem('token_expires_at')
      if (expiresAt) {
        const isExpired = Date.now() > parseInt(expiresAt)
        if (isExpired) {
          return false
        }
      }
      // If no expiration info, assume valid for Sanctum tokens
    } else {
      // Unknown token format
      return false
    }

    return true
  },

  // Detect token type (JWT vs Laravel Sanctum)
  detectTokenType(token: string): 'jwt' | 'sanctum' | 'unknown' {
    if (!token) return 'unknown'

    // JWT tokens have 3 parts separated by dots
    if (token.split('.').length === 3) {
      return 'jwt'
    }

    // Laravel Sanctum tokens have format: {id}|{token}
    if (token.includes('|') && /^\d+\|/.test(token)) {
      return 'sanctum'
    }

    return 'unknown'
  },

  // Get stored user data
  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        return JSON.parse(userStr) as User
      } catch (error) {
        console.error('Error parsing stored user data:', error)
        localStorage.removeItem('user')
      }
    }
    return null
  },

  // Get stored token
  getStoredToken(): string | null {
    return localStorage.getItem('auth_token')
  },

  // Check if user has specific role
  hasRole(role: string): boolean {
    const user = this.getStoredUser()
    return user?.role === role
  },

  // Check if user is admin
  isAdmin(): boolean {
    return this.hasRole('admin')
  },

  // Check if user is operator
  isOperator(): boolean {
    return this.hasRole('operator')
  },

  // Check if user is conductor
  isConductor(): boolean {
    return this.hasRole('conductor')
  },

  // Check if user can access company data
  canAccessCompany(companyId: number): boolean {
    const user = this.getStoredUser()
    if (!user) return false

    // Admins can access all companies
    if (user.role === 'admin') return true

    // Operators and conductors can only access their own company
    return user.company_id === companyId
  },

  // Refresh token
  async refreshToken(): Promise<RefreshTokenResponse> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    const response = await api.post<RefreshTokenResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    } as RefreshTokenRequest)

    // Update stored tokens
    localStorage.setItem('auth_token', response.token)
    localStorage.setItem('refresh_token', response.refresh_token)

    // Update token expiration info
    if (response.expires_in) {
      const expiresAt = Date.now() + response.expires_in * 1000
      localStorage.setItem('token_expires_at', expiresAt.toString())
    }

    return response
  },

  // Get stored refresh token
  getStoredRefreshToken(): string | null {
    return localStorage.getItem('refresh_token')
  },

  // Get token information
  getTokenInfo(): TokenInfo | null {
    const token = localStorage.getItem('auth_token')
    const refreshToken = localStorage.getItem('refresh_token')

    if (!token || !refreshToken) {
      return null
    }

    try {
      return getTokenInfo(token, refreshToken)
    } catch (error) {
      return null
    }
  },

  // Check if token needs refresh
  shouldRefreshToken(minutesBefore: number = 10): boolean {
    const token = localStorage.getItem('auth_token')
    if (!token) return false

    return shouldRefreshToken(token, minutesBefore)
  },

  // Update tokens after refresh
  updateTokens(tokenResponse: RefreshTokenResponse): void {
    localStorage.setItem('auth_token', tokenResponse.token)
    localStorage.setItem('refresh_token', tokenResponse.refresh_token)

    if (tokenResponse.expires_in) {
      const expiresAt = Date.now() + tokenResponse.expires_in * 1000
      localStorage.setItem('token_expires_at', expiresAt.toString())
    }

    // Update CSRF token if provided and CSRF is enabled
    if (tokenResponse.csrf_token && csrfService.isEnabled()) {
      csrfService.storeToken(tokenResponse.csrf_token, tokenResponse.expires_in)
    }
  },

  // Clear all authentication data
  clearAuthData(): void {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('token_expires_at')
    localStorage.removeItem('user')

    // Clear CSRF token
    csrfService.clearToken()
  },

  // Get combined token information (JWT + CSRF)
  getCombinedTokenInfo(): {
    jwt: TokenInfo | null
    csrf: CSRFTokenInfo | null
    isValid: boolean
    needsRefresh: boolean
  } | null {
    const token = localStorage.getItem('auth_token')
    const refreshToken = localStorage.getItem('refresh_token')

    if (!token || !refreshToken) return null

    try {
      const csrfTokenInfo = csrfService.getStoredTokenInfo()
      return getCombinedTokenInfo(token, refreshToken, csrfTokenInfo)
    } catch (error) {
      console.error('Failed to get combined token info:', error)
      return null
    }
  },

  // Get CSRF token status
  getCSRFStatus(): {
    enabled: boolean
    hasToken: boolean
    isExpired: boolean
    shouldRefresh: boolean
  } {
    return csrfService.getStatus()
  },
}
